import type { ActionTree } from 'vuex';
import type { MixedResult, BridgeError } from '@mx/pay-common';
import { setStorageItem, toJSON, normalizeError, delay, BizCommonError } from '@mx/pay-common';
import type { PayStatusParams, CashierPayErrorData } from '@/common';
import {
  env,
  bridge,
  monitor,
  metricPoints,
  QueryOrderStatus,
  SelectBankAction,
  PayResultStatus,
  CashierPayErrorType,
  CashierPayError,
} from '@/common';
import type {
  CashierState,
  DeferRootState,
  AfterPaySuccessPayload,
  MtpayBridgePayload,
  PaydelayCashierpayPayLoad,
} from '../constants';

export const actions: ActionTree<CashierState, DeferRootState> = {
  pay({ state, getters, commit, dispatch }, payload: PaydelayCashierpayPayLoad) {
    if (state.payBtnLoading) {
      return Promise.reject(new Error('截流中，暂时不允许继续请求'));
    } else {
      commit({ type: 'setBtnPending', btnPending: true });
      setTimeout(() => {
        commit({ type: 'setBtnPending', btnPending: false });
      }, 6000);
    }
    commit('setPayBtnLoading', { payBtnLoading: true });
    monitor.reportMetric(metricPoints.paySelectBank);
    monitor.captureInfo('唤起paydelay支付', {
      state: toJSON(state),
    });
    const mtpayPayInfo = { ...state.displayConsultData?.extendInfo } as { [key: string]: any };
    let payTransparentAttributesParse = {} as { [key: string]: any };
    let payTransparentAttributes = '';
    try {
      payTransparentAttributesParse = JSON.parse(mtpayPayInfo?.pay_transparent_attributes) ?? {};
      if (payTransparentAttributesParse && payTransparentAttributesParse?.need_open_delaypay) {
        delete payTransparentAttributesParse.need_open_delaypay;
      }
      payTransparentAttributes = JSON.stringify(
        Object.assign(payTransparentAttributesParse, {
          // 支付中签约美团的营销参数
          campaign_id: mtpayPayInfo?.campaign_id || '',
          delaypay_open_promo_info: state.marketInfoData?.delaypay_open_promo_info ?? '',
        })
      );
    } catch (error) {
      monitor.captureJSErrorMessage('解析payTransparentAttributes失败', { error: normalizeError(error) });
    }
    const cashier_payment = {
      ...mtpayPayInfo,
      pay_transparent_attributes: payTransparentAttributes,
      icon: {
        enable: 'https://p0.meituan.net/travelcube/e7f5550fb20e9146cc685fb96665fffc2303.png',
      },
      status: 0,
      status_info: '',
      selected: false,
      pay_type_unique_key: 'delaypay',
      use_np_pay: false,
      balance_combine_support: false,
      folded: false, // 是否折叠
      display_group: 'grp_qdb', // 区分美支或三方
      is_show_icon: false, // 是否展示icon
      recommend_support: true,
      amount: 5000,
      submit_url: '/cashier/gohellopay',
      name: '',
      pay_button: '开通并支付',
    };
    // 拼接参数
    const params = {
      cashier_payment,
      entry: 'payDelay',
      mt_payment: cashier_payment,
      ...env.inpayBasicBusinessParams,
      cashier_type: 'preorder-cashier', // 后端对应的cashier_type
    };
    return (
      dispatch<MtpayBridgePayload>({
        type: 'payByBridge',
        bridgeParams: params,
      })
        .finally(() => {
          commit('setPayBtnLoading', { payBtnLoading: false });
        })
        .then((res: MixedResult<PayStatusParams>) => {
          return handlePaymentResult(res.data);
        })
        .then((payBridgeResp: PayStatusParams) => {
          // eslint-disable-next-line promise/catch-or-return
          delay(100).then(() => {
            commit('setBtnPending', { btnPending: false });
          });
          dispatch<AfterPaySuccessPayload>({
            type: 'afterPaySuccess',
            delayPayCashierPaySuccess: payload.delayPayCashierPaySuccess,
            promotion:
              payBridgeResp?.pay_result?.status === PayResultStatus.PAY_SUCCESS
                ? payBridgeResp.pay_result.promotion
                : undefined,
          });
        }) // CashierPayError:支付过程中发生的错误，BizCommonError：接口请求返回的业务通用错误，
        // BridgeError:桥调用过程中发生的错误
        // iOS:delaypay报错native回调fail，gohellopay、cashdesk报错native回调success
        // pay-commom将fail回调的error包装成BridgeError
        .catch((error: CashierPayError | BizCommonError | BridgeError | Error) => {
          monitor.captureInfo({
            name: '支付过程取消或报错',
            msg: { error: normalizeError(error) },
          });
          // 拼好饭业务扣款的支付方式不是最终用户支付的支付方式，为了验证业务价值，暂时先将所有遇到的
          // 支付/签约失败的场景当做取消支付处理
          if (CashierPayError.isCashierPayError(error)) {
            const { type: errorType } = error as CashierPayError;
            switch (errorType) {
              case CashierPayErrorType.MTPayError:
                // Native 对错误做处理了
                monitor.reportMetric(metricPoints.tradeStatus, { tradeStatus: QueryOrderStatus.fail });
                // eslint-disable-next-line promise/catch-or-return
                delay(1000).then(() => {
                  payload.delayPaydowngradeStandardCashier();
                });
                break;
              case CashierPayErrorType.MTPayCancel:
                // 用户取消
                payload.delayPaydowngradeStandardCashier();
                break;
              default:
                // Native 对错误不做处理，h5 需要做提示
                monitor.reportMetric(metricPoints.tradeStatus, { tradeStatus: QueryOrderStatus.fail });
                commit({ type: 'setToast', show: true });
                // eslint-disable-next-line promise/catch-or-return
                delay(2000).then(() => {
                  commit({ type: 'setToast', show: false });
                  payload.delayPaydowngradeStandardCashier();
                });
            }
          } else if (BizCommonError.isBizCommonError(error)) {
            // 处理业务错误
            // native 对错误不做处理，h5 需要做提示
            monitor.reportMetric(metricPoints.tradeStatus, { tradeStatus: QueryOrderStatus.fail });
            commit({ type: 'setToast', show: true });
            // eslint-disable-next-line promise/catch-or-return
            delay(2000).then(() => {
              commit({ type: 'setToast', show: false });
              payload.delayPaydowngradeStandardCashier();
            });
          } else {
            monitor.reportMetric(metricPoints.tradeStatus, { tradeStatus: QueryOrderStatus.fail });
            // cashdesk、delaypay返回失败的回调,且被pay-commom包装成BridgeError，暂时看不出status类型，风险：可能区分不出真正调桥失败或接口失败场景
            // eslint-disable-next-line promise/catch-or-return
            delay(1000).then(() => {
              payload.delayPaydowngradeStandardCashier();
            });
          }
        })
    );
  },
  payByBridge({ rootState, rootGetters, commit }, payload: MtpayBridgePayload) {
    return bridge.useMtpayPayment(SelectBankAction.StartGoHelloPay, payload.bridgeParams);
  },
  afterPaySuccess({ state, getters, commit, dispatch }, payload: AfterPaySuccessPayload) {
    monitor.captureInfo('[info]afterPaySuccess', { res: toJSON(payload.promotion) });
    monitor.reportMetric(metricPoints.tradeStatus, { tradeStatus: QueryOrderStatus.success });
    setStorageItem('PAY_DELAY_SUCCESS_TRADENO', env.inpayBasicBusinessParams.tradeno);
    return payload.delayPayCashierPaySuccess(payload.promotion);
  },
};
function handlePaymentResult(payResp: PayStatusParams): Promise<PayStatusParams> {
  // 支付结果处理
  // 成功、失败、刷新、取消、超时、报错、过载等状态
  return Promise.resolve().then(() => {
    let payResult;
    try {
      payResult = payResp.pay_result;
    } catch (error) {
      throw new Error('payResp.pay_result报错了');
    }
    monitor.captureInfo('支付结果上报', { status: payResult.status });
    let errorType = CashierPayErrorType.MTPayError;
    const errorData: CashierPayErrorData = {
      ...payResult.data,
      status: payResult.status,
    };
    switch (payResult.status) {
      case PayResultStatus.PAY_SUCCESS:
        // eslint-disable-next-line promise/no-return-wrap
        return Promise.resolve(payResp);
      // 安卓cashdesk、delaypay接口请求失败（level1-6）的时候会走
      case PayResultStatus.PAY_FAIL:
      case PayResultStatus.PAY_OVERTIME:
      case PayResultStatus.PAY_FATAL_ERROR:
      case PayResultStatus.FATAL_ERROR:
        errorData.message = '支付失败，请稍后重试';
        errorType = CashierPayErrorType.MTPayError;
        break;
      case PayResultStatus.PAY_EXCEPTION:
        // 双端gohellopay接口失败（level1-6）的时候返回pay_exception
        if (!payResult.data) {
          errorType = CashierPayErrorType.MTPayError;
          break;
        }
        // 兼容 Native 个别情况下发 {data:{data:{xx}}} 这种特殊结构
        let err: ErrorType;
        if ('data' in payResult.data) {
          err = payResult.data.data;
        } else {
          err = payResult.data;
        }
        const { code, level, message } = err || {};
        // eslint-disable-next-line promise/no-return-wrap
        return Promise.reject(new BizCommonError(code, level, message, err));
      case PayResultStatus.NATIVE_EXCEPTION:
      case PayResultStatus.GO_HELLO_PAY_OVERLOAD:
      case PayResultStatus.ERROR:
        errorData.message = '支付失败，请稍后重试';
        errorType = CashierPayErrorType.PayOverload;
        break;
      case PayResultStatus.PAY_CANCEL:
      case PayResultStatus.SELECT_BANK_CLOSE:
        errorType = CashierPayErrorType.MTPayCancel;
        break;
      default:
        errorType = CashierPayErrorType.PayOverload;
        break;
    }
    // eslint-disable-next-line promise/no-return-wrap, no-restricted-globals
    return Promise.reject(new CashierPayError(errorType, errorData, status));
  });
}
